import { ErrorLogComponent } from '@cian/error-log-component';
import * as React from 'react';
import { useSelector } from 'react-redux';

import { CardSectionNew } from 'shared/components/CardSectionNew';
import { SimilarOffersAnchor } from 'shared/components/SimilarOffersSection';
import { SimilarNewObjectsContainer } from 'shared/components/similar_newobjects/container';
import { useApplicationContext } from 'shared/containers/ApplicationContext';
import { AuthorPremiumContainer } from 'shared/containers/AuthorPremiumContainer';
import { DeveloperInfoContainer } from 'shared/containers/BottomAuthorBlockNew/parts';
import { TourContainer } from 'shared/containers/TourContainer';
import { selectIsOfferGalleryRedesignExperimentActive } from 'shared/selectors/abExperiments';
import { selectIsNewbuildingPremiumRate } from 'shared/selectors/newbuildingPremiumRate';

import { FeedbackComplaintPopupContainer } from '../../FeedbackExperiment/components/PopupWithComplaint/ComplaintPopup/container';
import { FeedbackPopupContainer as FeedBackPopupC } from '../../FeedbackExperiment/components/PopupWithComplaint/FeedbackPopup';
import { PopupFeedbackSentContainer } from '../../FeedbackExperiment/components/PopupWithForm/PopupFeedbackSent';
import { LabelsLayoutNew } from '../../components/LabelsLayout';
import {
  OfferCardPageLayout,
  OfferCardPageLayoutCenter,
  OfferCardPageLayoutFooter,
  OfferCardPageLayoutTop,
  OfferCardPageLayoutAside,
} from '../../components/OfferCardPageLayout';
import { OfferHistoryContainer } from '../../components/OfferHistory/containers';
import { AdminButtonsContainer } from '../../components/admin_buttons/container';
import { HeaderAdminPanelContainer } from '../../components/header_admin_panel/container';
import { OfferBreadcrumbsContainer } from '../../components/offer_breadcrumbs/container';
import { OfferIsHiddenBlock } from '../../components/offer_hidden_block';
import { OfferRefusedContainer } from '../../components/offer_refused/container';
import { OfferUnpublishedContainer } from '../../components/offer_unpublished/container';
import { HideOfferTopPopup } from '../../components/popups/hide_offer_top_popup';
import { LegendModalContainer } from '../../components/popups/legend/container';
import { PrintModalContainer } from '../../components/popups/print/container';
import { RemoveModalContainer } from '../../components/popups/remove/container';
import { PrintInformationContainer } from '../../components/print_information/container';
import { PrintPhotoContainer } from '../../components/print_photo/container';
import { getOffer } from '../../selectors/newObject';
import { isNewbuildingFromDeveloperSelector } from '../../selectors/offerData/offer/isNewbuildingFromDeveloperSelector';
import { IState } from '../../store';
import { TgbBottomBannersContainer } from '../Adfox/TgbBottomBannersContainer';
import { AsideRedesignedContainer } from '../AsideRedesigned/AsideRedesigned';
import { BuilderCallReasonsContainerLoadable } from '../BuilderCallReasons';
import { CallbackWidgetContainer } from '../CallbackWidgetContainer';
import { ChatContainer } from '../ChatContainer';
import { ChatPopupContainer } from '../ChatPopupContainer';
import { ComparisonNotificationLoadableContainer } from '../ComparisonNotificationContainer';
import { DealSaleLabelNewContainer } from '../DealSaleLabelContainer';
import { DescriptionContainer } from '../Description/Description';
import { FavoritesFoldersContainer } from '../FavoritesFoldersContainer';
import { FlatViewOrderTooltipContainer } from '../FlatViewOrderTooltip';
import { HeatIndexContainer } from '../HeatIndexContainer';
import { IdenticalOffersContainer } from '../IdenticalOffersContainer';
import { MainNewContainer } from '../MainContainer';
import { NavigationContainerNew } from '../NavigationNew';
import NewbuildingAdvantages from '../NewbuildingAdvantages/NewbuildingAdvantages';
import { NewbuildingBookedFromDeveloperModalContainer } from '../NewbuildingBookedFromDeveloperModalContainer';
import { NewbuildingBookingModalContainer } from '../NewbuildingBookingModalContainer';
import { NewbuildingDecorationsContainer } from '../NewbuildingDecorations/NewbuildingDecorations';
import { GalleryContainer } from '../NewbuildingGalleryContainer';
import { NewbuildingMortgageSectionLoadable } from '../NewbuildingMortgageSection';
import { NewbuildingPromosSectionContainer } from '../NewbuildingPromosSection';
import { NewbuildingStatsContainer } from '../NewbuildingStats/NewbuildingStats';
import { NewbuildingTiledCarouselContainerLoadable } from '../NewbuildingTiledCarousel';
import { NewbuildingsFromDeveloperLazy } from '../NewbuildingsFromDeveloper';
import { NotAvailablePopupContainer } from '../NotAvailablePopupContainer';
import { ObjectFactoidsContainer } from '../ObjectFactoid/ObjectFactoid';
import { ObjectSummaryTitleContainer } from '../ObjectSummaryTitle/ObjectSummaryTitleContainer';
import { OfferMapContainer } from '../OfferMapContainer';
import { OfferMetaDataContainer } from '../OfferMetaData/OfferMetaData';
import { OfferSummaryInfoNewV2 } from '../OfferSummaryInfoNew';
import { OfferValuationContainer } from '../OfferValuation';
import { GoodPriceLabelNewContainer } from '../OfferValuation/containers/GoodPriceLabel';
import { OnlyCianLabelNewContainer } from '../OnlyCianLabelContainer';
import { PhoneWarningModalContainer } from '../PhoneWarningModalContainer';
import { RosreestrFlatSaleContainerNew } from '../RosreestrFlatSaleContainer';
import { RosreestrInfoContainerNew } from '../RosreestrInfoContainerNew';
import { SeoLinksContainer } from '../SeoLinks/SeoLinksContainer';
import { SimilarOffersGroupsContainer } from '../SimilarOffersRedesigned';
import { WarningsContainer } from '../WarningsContainer';
// TODO: Для перезаписи фона страницы, удалить файл при раскатке эксперимента
// eslint-disable-next-line import/order
import './styles.css';

const NewbuildingPageContainer = () => {
  const context = useApplicationContext();
  const hiddenObjectsLkUrl = context.config.get<string>('hiddenObjects.lkUrl') || '';
  const isGalleryRedesignEnabled = useSelector(selectIsOfferGalleryRedesignExperimentActive);
  const offer = useSelector(getOffer);
  const isPublished = offer.status === 'published';
  const isNewbuildingFromDeveloper = useSelector(isNewbuildingFromDeveloperSelector);
  const isHiddenByUser = useSelector((state: IState) => state.offerData.isHiddenByUser);
  const isTopPopupOpened = useSelector((state: IState) => state.hideOffer.isTopPopupOpened);
  const hideOfferStatus = useSelector((state: IState) => state.hideOffer.status);
  const hideOfferErrorMessage = useSelector((state: IState) => state.hideOffer.errorMessage);
  const isNewbuildingPremium = useSelector(selectIsNewbuildingPremiumRate);

  return (
    <>
      <NavigationContainerNew />
      <OfferCardPageLayout>
        <FeedBackPopupC />
        <FeedbackComplaintPopupContainer />

        <PopupFeedbackSentContainer />
        <FlatViewOrderTooltipContainer />
        <OfferCardPageLayoutTop>
          <OfferBreadcrumbsContainer />
          <OfferMetaDataContainer />
          <HeaderAdminPanelContainer />
          <OfferRefusedContainer config={context.config} />
          <OfferUnpublishedContainer />
          <AdminButtonsContainer />
          <ErrorLogComponent logger={context.logger}>
            <WarningsContainer />
          </ErrorLogComponent>
          {!isPublished && <SimilarOffersGroupsContainer />}
        </OfferCardPageLayoutTop>

        {isTopPopupOpened && (
          <HideOfferTopPopup
            status={hideOfferStatus}
            hidden={Boolean(isHiddenByUser)}
            lkUrl={hiddenObjectsLkUrl}
            hideOfferErrorMessage={hideOfferErrorMessage}
          />
        )}

        {isHiddenByUser && <OfferIsHiddenBlock lkUrl={hiddenObjectsLkUrl} />}

        <OfferCardPageLayoutCenter>
          <ErrorLogComponent logger={context.logger}>
            <LabelsLayoutNew>
              <GoodPriceLabelNewContainer />
              <OnlyCianLabelNewContainer />
              <DealSaleLabelNewContainer />
            </LabelsLayoutNew>
            <MainNewContainer />
          </ErrorLogComponent>
          {isPublished && (
            <div>
              {isGalleryRedesignEnabled && (
                <CardSectionNew hideForPrint>
                  <GalleryContainer reactErrorLogger={context.logger} />
                </CardSectionNew>
              )}
              {!isGalleryRedesignEnabled && isNewbuildingFromDeveloper && (
                <NewbuildingTiledCarouselContainerLoadable openTourInPopUp={true} />
              )}
              <PrintPhotoContainer />
            </div>
          )}

          <ObjectSummaryTitleContainer />
          <ObjectFactoidsContainer />
          <DescriptionContainer />
          <BuilderCallReasonsContainerLoadable />
          <OfferSummaryInfoNewV2 />
          <RosreestrFlatSaleContainerNew />
          <RosreestrInfoContainerNew />
          <NewbuildingDecorationsContainer />
          <CardSectionNew hideForPrint>
            <TourContainer />
          </CardSectionNew>
          {isPublished && [
            <div key="published-data">
              <NewbuildingAdvantages />
              <NewbuildingPromosSectionContainer isRedesign={true} />
              <NewbuildingMortgageSectionLoadable header="Ипотечные предложения" showHeader={true} />
              <ErrorLogComponent logger={context.logger}>
                <OfferMapContainer />
              </ErrorLogComponent>
              <NewbuildingStatsContainer />
              <HeatIndexContainer />
              <OfferValuationContainer />
              <OfferHistoryContainer />
              <ErrorLogComponent logger={context.logger}>
                <CardSectionNew hideForPrint id="author">
                  {isNewbuildingPremium && <AuthorPremiumContainer />}
                  {!isNewbuildingPremium && <DeveloperInfoContainer />}
                </CardSectionNew>
              </ErrorLogComponent>
            </div>,
          ]}
        </OfferCardPageLayoutCenter>

        <OfferCardPageLayoutAside>
          <AsideRedesignedContainer />
        </OfferCardPageLayoutAside>
      </OfferCardPageLayout>
      <OfferCardPageLayoutFooter>
        <NewbuildingsFromDeveloperLazy />
        <IdenticalOffersContainer />
        {isPublished && (
          <SimilarOffersAnchor>
            <SimilarOffersGroupsContainer />
          </SimilarOffersAnchor>
        )}
        <SeoLinksContainer />
        <SimilarNewObjectsContainer />
        <TgbBottomBannersContainer />
      </OfferCardPageLayoutFooter>
      <CallbackWidgetContainer />
      <PrintModalContainer />
      <PrintInformationContainer />
      <LegendModalContainer />
      <RemoveModalContainer />
      <ChatContainer config={context.config} />
      <ChatPopupContainer />
      <NotAvailablePopupContainer />
      <FavoritesFoldersContainer />
      <NewbuildingBookingModalContainer />
      <NewbuildingBookedFromDeveloperModalContainer />
      <PhoneWarningModalContainer />
      <ComparisonNotificationLoadableContainer />
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default NewbuildingPageContainer;
